import { Vector2 } from '@shared/types';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { CustomSkill, SkillCreator, HotbarSkill, isSkillV2 } from '@shared/skills/SkillCreator';
import { SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import type { Enemy } from '@client/entities/Enemy';

const ELEMENT_COLORS: Record<string, string> = {
  physical: '#ffcc88',
  gok: '#88ccff',
  od: '#ff8844',
  su: '#44ddff',
  karanlik: '#cc88ff',
};

const CIRCLE_ELEMENT_COLORS: Record<string, string> = {
  physical: '#cccccc',
  gok: '#5588cc',
  od: '#cc5544',
  su: '#44aacc',
  karanlik: '#9955cc',
};

const CONE_ELEMENT_COLORS: Record<string, string> = {
  physical: '#ffffff',
  gok: '#7eb8da',
  od: '#da7e7e',
  su: '#7edada',
  karanlik: '#b87eda',
};

const BRIGHT_COLORS: Record<string, string> = {
  physical: '#ffffff',
  gok: '#aaddff',
  od: '#ffaa88',
  su: '#88eeff',
  karanlik: '#ddaaff',
};

export interface SkillEffectConfig {
  skill: HotbarSkill;
  origin: Vector2;
  direction: Vector2;
  targetPosition?: Vector2;
}

export abstract class SkillEffect {
  protected skill: HotbarSkill;
  protected origin: Vector2;
  protected direction: Vector2;
  protected targetPosition: Vector2;
  protected life: number;
  protected maxLife: number;
  public isComplete: boolean = false;

  constructor(config: SkillEffectConfig) {
    this.skill = config.skill;
    this.origin = { ...config.origin };
    this.direction = { ...config.direction };
    this.targetPosition = config.targetPosition || { ...config.origin };
    this.maxLife = 0.3; // Default duration
    this.life = this.maxLife;
  }

  abstract update(deltaTime: number): void;
  abstract render(renderer: Renderer, camera: Camera): void;

  getProgress(): number {
    return 1 - this.life / this.maxLife;
  }

  setDuration(duration: number): void {
    this.maxLife = duration;
    this.life = duration;
  }

  /** Get world position for viewport culling */
  getWorldPosition(): Vector2 {
    return this.origin;
  }
}

// Slash effect for melee attacks
export class SlashEffect extends SkillEffect {
  private angle: number;
  private radius: number;
  private arcAngle: number;
  private trails: { angle: number; radius: number; alpha: number }[] = [];
  private minimal = false; // If true, render just a small hit line instead of full arc

  // Class-level gradient cache keyed by element color
  private static gradientCache = new Map<string, { canvas: HTMLCanvasElement; zoom: number; radius: number }>();

  setMinimal(v: boolean): void { this.minimal = v; if (v) this.maxLife = 0.15; this.life = this.maxLife; }

  constructor(config: SkillEffectConfig) {
    super(config);
    this.maxLife = 0.25;
    this.life = this.maxLife;

    this.angle = Math.atan2(this.direction.y, this.direction.x);

    // Get effective range based on skill version
    this.radius = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveRange(this.skill)
      : SkillCreator.getEffectiveRange(this.skill as CustomSkill);

    // Arc width depends on skill type
    if (isSkillV2(this.skill)) {
      const meleeType = (this.skill as any).meleeType || 'strike';
      if (meleeType === 'thrust') {
        this.arcAngle = Math.PI / 6; // 30° narrow thrust
      } else {
        this.arcAngle = Math.PI / 3; // 60° focused strike (not wide cleave)
      }
    } else {
      const areaValue = (this.skill as CustomSkill).sliders.area;
      this.arcAngle = (Math.PI / 4) + (areaValue / 100) * (Math.PI * 3 / 4);
    }
  }

  update(deltaTime: number): void {
    this.life -= deltaTime;

    // Add trail
    const progress = this.getProgress();
    this.trails.push({
      angle: this.angle,
      radius: this.radius * (0.5 + progress * 0.5),
      alpha: 1 - progress,
    });

    // Fade trails (swap-and-pop to avoid array allocation)
    for (let i = this.trails.length - 1; i >= 0; i--) {
      this.trails[i].alpha -= deltaTime * 4;
      if (this.trails[i].alpha <= 0) {
        this.trails[i] = this.trails[this.trails.length - 1];
        this.trails.pop();
      }
    }

    if (this.life <= 0) {
      this.isComplete = true;
    }
  }

  /** Get or create cached radial gradient for slash arc fill */
  private getSlashGradientCanvas(color: string, radius: number, zoom: number): HTMLCanvasElement {
    const bucketR = Math.round(radius / 10) * 10 || 10;
    const bucketZoom = Math.round(zoom * 50) / 50;
    const key = `${color}_${bucketR}_${bucketZoom}`;
    const cached = SlashEffect.gradientCache.get(key);
    if (cached) {
      return cached.canvas;
    }

    const size = Math.ceil(bucketR * 2) + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    // Pre-render the gradient at full alpha; actual alpha applied via globalAlpha
    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, bucketR);
    grad.addColorStop(0, `${color}00`);
    grad.addColorStop(0.6, `${color}3c`); // ~60/255
    grad.addColorStop(1, `${color}78`);   // ~120/255

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, bucketR, 0, Math.PI * 2);
    ctx.fill();

    // Limit cache size to prevent unbounded growth
    if (SlashEffect.gradientCache.size > 50) {
      const firstKey = SlashEffect.gradientCache.keys().next().value;
      if (firstKey) SlashEffect.gradientCache.delete(firstKey);
    }
    SlashEffect.gradientCache.set(key, { canvas, zoom: bucketZoom, radius: bucketR });
    return canvas;
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = camera.worldToScreenInPlace(this.origin.x, this.origin.y);
    const sx = screenPos.x;
    const sy = screenPos.y;
    const progress = this.getProgress();

    const ctx = renderer.context;
    ctx.save();

    const element = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveElement(this.skill)
      : SkillCreator.getEffectiveElement(this.skill as CustomSkill);
    const color = this.getElementColor(element);

    // Minimal mode: short hit line only, no arc/cone
    if (this.minimal) {
      const alpha = 1 - progress;
      const len = this.radius * camera.zoom * (0.3 + progress * 0.7);
      ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.9})`;
      ctx.lineWidth = 3 * camera.zoom;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(sx + Math.cos(this.angle) * 10 * camera.zoom, sy + Math.sin(this.angle) * 10 * camera.zoom);
      ctx.lineTo(sx + Math.cos(this.angle) * len, sy + Math.sin(this.angle) * len);
      ctx.stroke();
      ctx.strokeStyle = `${color}${Math.floor(alpha * 150).toString(16).padStart(2, '0')}`;
      ctx.lineWidth = 6 * camera.zoom;
      ctx.stroke();
      ctx.restore();
      return;
    }

    // Draw trails
    for (const trail of this.trails) {
      const startAngle = trail.angle - this.arcAngle / 2;
      const endAngle = trail.angle + this.arcAngle / 2;
      const trailRadius = trail.radius * camera.zoom;

      ctx.beginPath();
      ctx.arc(sx, sy, trailRadius, startAngle, endAngle);
      ctx.strokeStyle = `${color}${Math.floor(trail.alpha * 80).toString(16).padStart(2, '0')}`;
      ctx.lineWidth = 2 * camera.zoom;
      ctx.stroke();
    }

    // Main slash arc
    const startAngle = this.angle - this.arcAngle / 2;
    const endAngle = this.angle + this.arcAngle / 2;
    const currentRadius = this.radius * camera.zoom * (0.5 + progress * 0.5);
    const alpha = 1 - progress;

    // Arc fill with cached gradient, clipped to arc shape
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.arc(sx, sy, currentRadius, startAngle, endAngle);
    ctx.closePath();
    ctx.clip();

    const slashCanvas = this.getSlashGradientCanvas(color, currentRadius, camera.zoom);
    const half = slashCanvas.width / 2;
    ctx.globalAlpha = alpha;
    ctx.drawImage(slashCanvas, sx - half, sy - half);
    ctx.restore();

    // Bright edge
    ctx.beginPath();
    ctx.arc(sx, sy, currentRadius, startAngle, endAngle);
    ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.8})`;
    ctx.lineWidth = 3 * camera.zoom;
    ctx.lineCap = 'round';
    ctx.stroke();

    // Inner glow line
    ctx.beginPath();
    ctx.arc(sx, sy, currentRadius * 0.9, startAngle, endAngle);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2 * camera.zoom;
    ctx.stroke();

    ctx.restore();
  }

  private getElementColor(element: string): string {
    return ELEMENT_COLORS[element] || '#ffcc88';
  }
}

// Circle/AoE effect - Ground travel + Explosion style
export class CircleEffect extends SkillEffect {
  private radius: number;
  private effectPosition: Vector2;
  private color: string;
  private brightColor: string;
  private travelTime: number = 0.3; // Time for ground travel phase

  // Class-level gradient cache for circle effect
  private static bulgeCache = new Map<string, { canvas: HTMLCanvasElement; zoom: number }>();

  constructor(config: SkillEffectConfig) {
    super(config);

    this.radius = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveArea(this.skill)
      : SkillCreator.getEffectiveArea(this.skill as CustomSkill);

    // Circle AOE should appear at target position (mouse), not at player origin
    this.effectPosition = config.targetPosition || config.origin;

    // Total animation time: travel + explosion
    let explosionDuration = 0.5;
    if (isSkillV2(this.skill)) {
      const trigger = (this.skill as any).aoeTrigger || 'instant';
      if (trigger === 'persistent') {
        // Persistent: visual lasts the full duration
        explosionDuration = SkillCreatorV2.getAOEDuration(this.skill);
      } else {
        explosionDuration = 0.5;
      }
    }
    this.maxLife = this.travelTime + explosionDuration;
    this.life = this.maxLife;

    const element = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveElement(this.skill)
      : SkillCreator.getEffectiveElement(this.skill as CustomSkill);
    this.color = this.getElementColor(element);
    this.brightColor = this.getBrightColor(element);
  }

  override getWorldPosition(): Vector2 {
    return this.effectPosition;
  }

  update(deltaTime: number): void {
    this.life -= deltaTime;
    if (this.life <= 0) {
      this.isComplete = true;
    }
  }

  /** Get or create cached bulge gradient */
  private getBulgeCanvas(zoom: number): HTMLCanvasElement {
    const bucketZoom = Math.round(zoom * 50) / 50;
    const key = this.color;
    const cached = CircleEffect.bulgeCache.get(key);
    if (cached && cached.zoom === bucketZoom) return cached.canvas;

    const refRadius = Math.ceil(20 * zoom);
    const size = refRadius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, refRadius);
    grad.addColorStop(0, `${this.brightColor}cc`);
    grad.addColorStop(0.4, `${this.color}88`);
    grad.addColorStop(1, `${this.color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, refRadius, 0, Math.PI * 2);
    ctx.fill();

    CircleEffect.bulgeCache.set(key, { canvas, zoom: bucketZoom });
    return canvas;
  }

  render(renderer: Renderer, camera: Camera): void {
    const ctx = renderer.context;
    ctx.save();

    const elapsed = this.maxLife - this.life;
    const targetScreen = camera.worldToScreen(this.effectPosition.x, this.effectPosition.y);
    const originScreen = camera.worldToScreen(this.origin.x, this.origin.y);

    // Phase 1: Ground travel (0 to travelTime)
    if (elapsed < this.travelTime) {
      const travelProgress = elapsed / this.travelTime;
      this.renderGroundTravel(ctx, originScreen, targetScreen, travelProgress, camera.zoom);
    }

    // Phase 2: Explosion (after travelTime)
    if (elapsed >= this.travelTime) {
      const explosionDuration = this.maxLife - this.travelTime;
      const explosionProgress = (elapsed - this.travelTime) / Math.max(0.1, explosionDuration);
      this.renderExplosion(ctx, targetScreen, explosionProgress, camera.zoom);
    }

    ctx.restore();
  }

  private renderGroundTravel(
    ctx: CanvasRenderingContext2D,
    origin: Vector2,
    target: Vector2,
    progress: number,
    zoom: number
  ): void {
    const dx = target.x - origin.x;
    const dy = target.y - origin.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Current position of the traveling effect
    const currentX = origin.x + dx * progress;
    const currentY = origin.y + dy * progress;

    // Ground crack trail
    const trailLength = Math.min(distance * progress, 80 * zoom);
    const trailStartX = currentX - (dx / distance) * trailLength;
    const trailStartY = currentY - (dy / distance) * trailLength;

    // Draw ground cracks behind the traveling point
    ctx.strokeStyle = `${this.color}aa`;
    ctx.lineWidth = 3 * zoom;
    ctx.lineCap = 'round';

    // Main crack line
    ctx.beginPath();
    ctx.moveTo(trailStartX, trailStartY);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();

    // Side cracks
    const crackCount = 5;
    for (let i = 0; i < crackCount; i++) {
      const t = (i + 1) / (crackCount + 1);
      const crackX = trailStartX + (currentX - trailStartX) * t;
      const crackY = trailStartY + (currentY - trailStartY) * t;

      // Perpendicular direction
      const perpX = -dy / distance;
      const perpY = dx / distance;

      const side = (i % 2 === 0) ? 1 : -1;
      const crackLength = (15 + Math.random() * 10) * zoom;

      ctx.beginPath();
      ctx.moveTo(crackX, crackY);
      ctx.lineTo(crackX + perpX * side * crackLength, crackY + perpY * side * crackLength);
      ctx.strokeStyle = `${this.color}66`;
      ctx.lineWidth = 2 * zoom;
      ctx.stroke();
    }

    // Traveling "bulge" effect - cached gradient
    const bulgeCanvas = this.getBulgeCanvas(zoom);
    const bHalf = bulgeCanvas.width / 2;
    ctx.drawImage(bulgeCanvas, currentX - bHalf, currentY - bHalf);

    // Small rocks/debris particles around the traveling point
    const debrisCount = 6;
    for (let i = 0; i < debrisCount; i++) {
      const angle = (i / debrisCount) * Math.PI * 2 + progress * 10;
      const debrisDistance = (10 + Math.sin(progress * 20 + i) * 5) * zoom;
      const debrisX = currentX + Math.cos(angle) * debrisDistance;
      const debrisY = currentY + Math.sin(angle) * debrisDistance - Math.abs(Math.sin(progress * 15 + i * 2)) * 8 * zoom;
      const debrisSize = (2 + Math.random()) * zoom;

      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(debrisX, debrisY, debrisSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderExplosion(
    ctx: CanvasRenderingContext2D,
    position: Vector2,
    progress: number,
    zoom: number
  ): void {
    const fullRadius = this.radius * zoom;
    // Persistent AOE: stay visible most of the duration, fade only in last 20%
    const isPersistent = isSkillV2(this.skill) && ((this.skill as any).aoeTrigger === 'persistent');
    const alpha = isPersistent
      ? (progress > 0.8 ? Math.max(0, (1 - progress) * 5) : 1.0)
      : Math.max(0, 1 - progress);

    const element = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveElement(this.skill)
      : SkillCreator.getEffectiveElement(this.skill as CustomSkill);

    // Initial flash (0-30%)
    if (progress < 0.3) {
      const flashProgress = progress / 0.3;
      const flashRadius = fullRadius * 0.5 * (1 + flashProgress * 0.5);
      const flashAlpha = 1 - flashProgress;

      const flashGrad = ctx.createRadialGradient(
        position.x, position.y, 0,
        position.x, position.y, flashRadius
      );
      flashGrad.addColorStop(0, '#ffffff');
      flashGrad.addColorStop(0.2, `${this.brightColor}`);
      flashGrad.addColorStop(0.5, `${this.color}${Math.floor(flashAlpha * 200).toString(16).padStart(2, '0')}`);
      flashGrad.addColorStop(1, `${this.color}00`);

      ctx.beginPath();
      ctx.arc(position.x, position.y, flashRadius, 0, Math.PI * 2);
      ctx.fillStyle = flashGrad;
      ctx.fill();
    }

    // Expanding shockwave rings
    const waveCount = 3;
    for (let i = 0; i < waveCount; i++) {
      const waveDelay = i * 0.12;
      const waveProgress = Math.max(0, Math.min(1, (progress - waveDelay) / 0.5));

      if (waveProgress > 0 && waveProgress < 1) {
        const waveRadius = fullRadius * waveProgress;
        const waveAlpha = (1 - waveProgress) * (1 - i * 0.2);
        const lineWidth = (5 - i * 1.5) * zoom;

        ctx.beginPath();
        ctx.arc(position.x, position.y, waveRadius, 0, Math.PI * 2);
        ctx.strokeStyle = `${this.color}${Math.floor(waveAlpha * 200).toString(16).padStart(2, '0')}`;
        ctx.lineWidth = lineWidth;
        ctx.stroke();
      }
    }

    // Ground crater effect (isometric ellipse for depth)
    const craterRadius = fullRadius * Math.min(1, progress * 1.5);
    const craterGrad = ctx.createRadialGradient(
      position.x, position.y, 0,
      position.x, position.y, craterRadius
    );
    craterGrad.addColorStop(0, `${this.color}${Math.floor(alpha * 80).toString(16).padStart(2, '0')}`);
    craterGrad.addColorStop(0.5, `${this.color}${Math.floor(alpha * 50).toString(16).padStart(2, '0')}`);
    craterGrad.addColorStop(1, `${this.color}00`);

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(position.x, position.y, craterRadius, craterRadius * 0.55, 0, 0, Math.PI * 2);
    ctx.fillStyle = craterGrad;
    ctx.fill();
    ctx.restore();

    // Element-specific particle effects
    this.renderElementParticles(ctx, position, progress, zoom, fullRadius, element);

    // Bright edge ring
    if (progress > 0.1 && progress < 0.6) {
      const edgeProgress = (progress - 0.1) / 0.5;
      const edgeRadius = fullRadius * edgeProgress;
      const edgeAlpha = (1 - edgeProgress) * 0.8;

      ctx.beginPath();
      ctx.arc(position.x, position.y, edgeRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `${this.brightColor}${Math.floor(edgeAlpha * 200).toString(16).padStart(2, '0')}`;
      ctx.lineWidth = 3 * zoom;
      ctx.stroke();
    }

    // Lingering ground mark (fades out slowly at end of explosion)
    if (progress > 0.4) {
      const lingerAlpha = Math.max(0, (1 - progress) * 0.4);
      ctx.save();
      ctx.globalAlpha = lingerAlpha;
      ctx.beginPath();
      ctx.ellipse(position.x, position.y, fullRadius * 0.8, fullRadius * 0.44, 0, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.fill();
      // Scorch/crack marks
      ctx.strokeStyle = `${this.color}88`;
      ctx.lineWidth = 1.5 * zoom;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const len = fullRadius * (0.3 + (i % 3) * 0.15);
        ctx.beginPath();
        ctx.moveTo(position.x, position.y);
        ctx.lineTo(position.x + Math.cos(a) * len, position.y + Math.sin(a) * len * 0.55);
        ctx.stroke();
      }
      ctx.restore();
    }
  }

  /** Element-specific particle effects during explosion */
  private renderElementParticles(
    ctx: CanvasRenderingContext2D,
    position: Vector2,
    progress: number,
    zoom: number,
    fullRadius: number,
    element: string
  ): void {
    if (progress >= 0.8) return;
    const debrisProgress = progress / 0.8;
    const debrisAlpha = 1 - debrisProgress;
    const count = 12;

    switch (element) {
      case 'od': {
        // Fire: rising sparks/embers that float upward
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dist = fullRadius * debrisProgress * (0.6 + (i % 3) * 0.25);
          const rise = debrisProgress * 40 * zoom * (1 + (i % 4) * 0.3);
          const flicker = Math.sin(debrisProgress * 30 + i * 5) * 3 * zoom;
          const px = position.x + Math.cos(angle) * dist + flicker;
          const py = position.y + Math.sin(angle) * dist - rise;
          const size = (3 - debrisProgress * 2.5) * zoom;
          if (size > 0) {
            ctx.fillStyle = i % 3 === 0
              ? `rgba(255, 200, 50, ${debrisAlpha})`
              : `rgba(255, 100, 30, ${debrisAlpha * 0.8})`;
            ctx.beginPath();
            ctx.arc(px, py, size, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        break;
      }
      case 'su': {
        // Ice: angular crystal shards flying outward
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dist = fullRadius * debrisProgress * (0.7 + (i % 2) * 0.3);
          const height = Math.sin(debrisProgress * Math.PI) * 20 * zoom;
          const px = position.x + Math.cos(angle) * dist;
          const py = position.y + Math.sin(angle) * dist - height;
          const size = (4 - debrisProgress * 3) * zoom;
          if (size > 0.5) {
            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(angle + debrisProgress * 4);
            ctx.fillStyle = `rgba(140, 220, 255, ${debrisAlpha})`;
            // Diamond/crystal shape
            ctx.beginPath();
            ctx.moveTo(0, -size);
            ctx.lineTo(size * 0.5, 0);
            ctx.lineTo(0, size * 0.6);
            ctx.lineTo(-size * 0.5, 0);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }
        }
        break;
      }
      case 'gok': {
        // Lightning: branching electric lines
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2 + debrisProgress * 2;
          const len = fullRadius * (0.3 + debrisProgress * 0.6);
          ctx.strokeStyle = `rgba(130, 200, 255, ${debrisAlpha * 0.9})`;
          ctx.lineWidth = (2 - debrisProgress) * zoom;
          ctx.beginPath();
          ctx.moveTo(position.x, position.y);
          // Jagged path
          let cx = position.x, cy = position.y;
          const segs = 4;
          for (let s = 1; s <= segs; s++) {
            const t = s / segs;
            const jitterX = (Math.sin(i * 7 + s * 13 + debrisProgress * 20) * 8) * zoom;
            const jitterY = (Math.cos(i * 11 + s * 17 + debrisProgress * 20) * 8) * zoom;
            cx = position.x + Math.cos(angle) * len * t + jitterX;
            cy = position.y + Math.sin(angle) * len * t + jitterY;
            ctx.lineTo(cx, cy);
          }
          ctx.stroke();
          // Bright endpoint spark
          if (debrisProgress < 0.5) {
            ctx.fillStyle = `rgba(255, 255, 255, ${debrisAlpha})`;
            ctx.beginPath();
            ctx.arc(cx, cy, 2 * zoom, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        break;
      }
      case 'karanlik': {
        // Dark/Chaos: swirling shadow tendrils + purple sparks
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2 + debrisProgress * 2;
          const len = fullRadius * debrisProgress;
          const beamWidth = (3 - debrisProgress * 2) * zoom;
          const beamAlpha = debrisAlpha * 0.6;
          ctx.save();
          ctx.translate(position.x, position.y);
          ctx.rotate(angle);
          ctx.fillStyle = `rgba(160, 80, 220, ${beamAlpha})`;
          ctx.fillRect(0, -beamWidth / 2, len, beamWidth);
          ctx.restore();
        }
        // Floating dark sparkles
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * Math.PI * 2 + debrisProgress * 3;
          const d = fullRadius * (0.3 + debrisProgress * 0.5);
          const rise = debrisProgress * 20 * zoom;
          const px = position.x + Math.cos(a) * d;
          const py = position.y + Math.sin(a) * d - rise;
          const sparkAlpha = debrisAlpha * (0.5 + Math.sin(debrisProgress * 20 + i * 3) * 0.5);
          ctx.fillStyle = `rgba(200, 150, 255, ${sparkAlpha})`;
          ctx.beginPath();
          ctx.arc(px, py, 1.5 * zoom, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      default: {
        // Physical: flying rocks/debris (original behavior)
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const dist = fullRadius * debrisProgress * (0.8 + (i % 3) * 0.2);
          const height = Math.sin(debrisProgress * Math.PI) * 30 * zoom;
          const px = position.x + Math.cos(angle) * dist;
          const py = position.y + Math.sin(angle) * dist - height;
          const size = (3 - debrisProgress * 2) * zoom * (1 + (i % 2) * 0.5);
          if (size > 0) {
            ctx.fillStyle = `${this.color}${Math.floor(debrisAlpha * 255).toString(16).padStart(2, '0')}`;
            ctx.beginPath();
            ctx.arc(px, py, size, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        break;
      }
    }
  }

  private getElementColor(element: string): string {
    return CIRCLE_ELEMENT_COLORS[element] || '#cccccc';
  }

  private getBrightColor(element: string): string {
    return BRIGHT_COLORS[element] || '#ffffff';
  }
}

// Cone AoE effect
export class ConeEffect extends SkillEffect {
  private coneAngle: number;
  private coneLength: number;

  // Class-level gradient cache for cone fills
  private static gradientCache = new Map<string, { canvas: HTMLCanvasElement; zoom: number; length: number }>();

  constructor(config: SkillEffectConfig) {
    super(config);
    this.maxLife = 0.4;
    this.life = this.maxLife;

    // Get cone parameters from V2 skill
    if (isSkillV2(this.skill)) {
      this.coneAngle = SkillCreatorV2.getAOEConeAngle(this.skill) * (Math.PI / 180); // Convert to radians
      this.coneLength = SkillCreatorV2.getAOEConeLength(this.skill);
    } else {
      // Fallback for V1
      this.coneAngle = Math.PI / 3; // 60 degrees
      this.coneLength = 150;
    }
  }

  update(deltaTime: number): void {
    this.life -= deltaTime;
    if (this.life <= 0) {
      this.isComplete = true;
    }
  }

  /** Get or create cached cone radial gradient */
  private getConeGradientCanvas(color: string, length: number, zoom: number): HTMLCanvasElement {
    const bucketL = Math.round(length / 10) * 10 || 10;
    const bucketZoom = Math.round(zoom * 50) / 50;
    const key = color;
    const cached = ConeEffect.gradientCache.get(key);
    if (cached && cached.zoom === bucketZoom && cached.length === bucketL) {
      return cached.canvas;
    }

    const size = Math.ceil(bucketL * 2) + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    // Pre-render with a moderate alpha; actual alpha modulated via globalAlpha
    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, bucketL);
    grad.addColorStop(0, `${color}50`);
    grad.addColorStop(0.6, `${color}32`);
    grad.addColorStop(1, `${color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, bucketL, 0, Math.PI * 2);
    ctx.fill();

    ConeEffect.gradientCache.set(key, { canvas, zoom: bucketZoom, length: bucketL });
    return canvas;
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = camera.worldToScreenInPlace(this.origin.x, this.origin.y);
    const sx = screenPos.x;
    const sy = screenPos.y;
    const progress = this.getProgress();

    const ctx = renderer.context;
    ctx.save();

    const element = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveElement(this.skill)
      : SkillCreator.getEffectiveElement(this.skill as CustomSkill);
    const color = this.getElementColor(element);

    // Calculate cone direction angle
    const dirAngle = Math.atan2(this.direction.y, this.direction.x);
    const halfAngle = this.coneAngle / 2;

    // Current cone length based on progress
    const currentLength = this.coneLength * camera.zoom * progress;
    const alpha = 1 - progress;

    // Draw cone fill with cached gradient, clipped to cone shape
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.arc(sx, sy, currentLength, dirAngle - halfAngle, dirAngle + halfAngle);
    ctx.closePath();
    ctx.clip();

    const coneCanvas = this.getConeGradientCanvas(color, currentLength, camera.zoom);
    const half = coneCanvas.width / 2;
    ctx.globalAlpha = alpha;
    ctx.drawImage(coneCanvas, sx - half, sy - half);
    ctx.restore();

    // Draw cone edges
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(
      sx + Math.cos(dirAngle - halfAngle) * currentLength,
      sy + Math.sin(dirAngle - halfAngle) * currentLength
    );
    ctx.arc(sx, sy, currentLength, dirAngle - halfAngle, dirAngle + halfAngle);
    ctx.lineTo(sx, sy);
    ctx.strokeStyle = `${color}${Math.floor(alpha * 200).toString(16).padStart(2, '0')}`;
    ctx.lineWidth = 2 * camera.zoom;
    ctx.stroke();

    // Draw bright arc at the edge
    ctx.beginPath();
    ctx.arc(sx, sy, currentLength, dirAngle - halfAngle, dirAngle + halfAngle);
    ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.6})`;
    ctx.lineWidth = 3 * camera.zoom;
    ctx.stroke();

    // Inner expanding wave effects
    for (let i = 1; i <= 3; i++) {
      const waveProgress = Math.max(0, progress - i * 0.1);
      if (waveProgress <= 0) continue;
      const waveLength = currentLength * (0.3 + waveProgress * 0.7);
      const waveAlpha = alpha * (1 - i * 0.25);

      ctx.beginPath();
      ctx.arc(sx, sy, waveLength, dirAngle - halfAngle, dirAngle + halfAngle);
      ctx.strokeStyle = `${color}${Math.floor(waveAlpha * 100).toString(16).padStart(2, '0')}`;
      ctx.lineWidth = (3 - i) * camera.zoom;
      ctx.stroke();
    }

    // Element-specific particles within cone
    if (progress < 0.8) {
      const particleCount = 6;
      for (let i = 0; i < particleCount; i++) {
        const pAngle = dirAngle + (Math.random() - 0.5) * this.coneAngle;
        const pDist = currentLength * (0.3 + Math.random() * 0.6);
        const px = sx + Math.cos(pAngle) * pDist;
        const py = sy + Math.sin(pAngle) * pDist;
        const pAlpha = alpha * (0.3 + Math.random() * 0.5);
        const pSize = (1 + Math.random() * 2) * camera.zoom;

        switch (element) {
          case 'od':
            ctx.fillStyle = `rgba(255, ${150 + Math.floor(Math.random() * 80)}, 50, ${pAlpha})`;
            break;
          case 'su':
            ctx.fillStyle = `rgba(150, 220, 255, ${pAlpha})`;
            break;
          case 'gok':
            ctx.fillStyle = `rgba(180, 220, 255, ${pAlpha})`;
            break;
          case 'karanlik':
            ctx.fillStyle = `rgba(220, 200, 255, ${pAlpha})`;
            break;
          default:
            ctx.fillStyle = `rgba(200, 200, 200, ${pAlpha})`;
        }
        ctx.beginPath();
        ctx.arc(px, py, pSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  private getElementColor(element: string): string {
    return CONE_ELEMENT_COLORS[element] || '#ffffff';
  }
}

// Projectile effect
export class ProjectileEffect extends SkillEffect {
  private position: Vector2;
  private speed: number = 500;
  private size: number = 8;
  private rotation: number = 0;

  // Circular buffer for trail positions (avoids shift() O(n))
  private trailBuffer: Vector2[];
  private trailHead: number = 0;
  private trailCount: number = 0;
  private static readonly TRAIL_MAX = 10;

  // Collision detection
  private getEnemies: (() => Enemy[]) | null = null;
  private hitRadius: number = 30;
  private hitEnemyIds: Set<string> = new Set();
  private pierceCount: number = 0;
  private hitCount: number = 0;
  private onHitEnemy: ((enemy: Enemy) => void) | null = null;

  // Class-level gradient caches
  private static outerGlowCache = new Map<string, { canvas: HTMLCanvasElement; zoom: number }>();
  private static coreGlowCache = new Map<string, { canvas: HTMLCanvasElement; zoom: number }>();

  constructor(config: SkillEffectConfig) {
    super(config);
    this.position = { ...this.origin };

    // Apply V2 speed slider
    if (isSkillV2(this.skill)) {
      this.speed = SkillCreatorV2.getProjectileSpeed(this.skill);
    }

    const effectiveRange = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveRange(this.skill)
      : SkillCreator.getEffectiveRange(this.skill as CustomSkill);
    this.maxLife = effectiveRange / this.speed;
    this.life = this.maxLife;

    // Pre-allocate circular buffer
    this.trailBuffer = new Array(ProjectileEffect.TRAIL_MAX);
    for (let i = 0; i < ProjectileEffect.TRAIL_MAX; i++) {
      this.trailBuffer[i] = { x: 0, y: 0 };
    }
  }

  override getWorldPosition(): Vector2 {
    return this.position;
  }

  setCollisionData(getEnemies: () => Enemy[], hitRadius: number, pierceCount: number, onHit: (enemy: Enemy) => void): void {
    this.getEnemies = getEnemies;
    this.hitRadius = hitRadius;
    this.pierceCount = pierceCount;
    this.onHitEnemy = onHit;
  }

  update(deltaTime: number): void {
    // Store trail position in circular buffer (O(1) instead of shift() O(n))
    const slot = this.trailBuffer[this.trailHead];
    slot.x = this.position.x;
    slot.y = this.position.y;
    this.trailHead = (this.trailHead + 1) % ProjectileEffect.TRAIL_MAX;
    if (this.trailCount < ProjectileEffect.TRAIL_MAX) this.trailCount++;

    // Move projectile
    this.position.x += this.direction.x * this.speed * deltaTime;
    this.position.y += this.direction.y * this.speed * deltaTime;

    // Rotate
    this.rotation += deltaTime * 10;

    // Collision detection (squared distance avoids sqrt)
    if (this.onHitEnemy && this.getEnemies) {
      const enemies = this.getEnemies();
      const hitRadiusSq = this.hitRadius * this.hitRadius;
      for (const enemy of enemies) {
        if (!enemy.isAlive || enemy.isDying) continue;
        if (this.hitEnemyIds.has(enemy.id)) continue;

        const dx = enemy.position.x - this.position.x;
        const dy = enemy.position.y - this.position.y;
        const distSq = dx * dx + dy * dy;

        if (distSq <= hitRadiusSq) {
          this.onHitEnemy(enemy);
          this.hitEnemyIds.add(enemy.id);
          this.hitCount++;

          if (this.hitCount > this.pierceCount) {
            this.isComplete = true;
            return;
          }
        }
      }
    }

    this.life -= deltaTime;
    if (this.life <= 0) {
      this.isComplete = true;
    }
  }

  /** Get or create cached outer glow canvas */
  private getOuterGlowCanvas(color: string, zoom: number): HTMLCanvasElement {
    const bucketZoom = Math.round(zoom * 50) / 50;
    const key = color;
    const cached = ProjectileEffect.outerGlowCache.get(key);
    if (cached && cached.zoom === bucketZoom) return cached.canvas;

    const radius = Math.ceil(this.size * 3 * zoom);
    const size = radius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, radius);
    grad.addColorStop(0, `${color}60`);
    grad.addColorStop(0.5, `${color}30`);
    grad.addColorStop(1, `${color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, radius, 0, Math.PI * 2);
    ctx.fill();

    ProjectileEffect.outerGlowCache.set(key, { canvas, zoom: bucketZoom });
    return canvas;
  }

  /** Get or create cached core glow canvas */
  private getCoreGlowCanvas(color: string, brightColor: string, zoom: number): HTMLCanvasElement {
    const bucketZoom = Math.round(zoom * 50) / 50;
    const key = color + brightColor;
    const cached = ProjectileEffect.coreGlowCache.get(key);
    if (cached && cached.zoom === bucketZoom) return cached.canvas;

    const radius = Math.ceil(this.size * 1.5 * zoom);
    const size = radius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, radius);
    grad.addColorStop(0, brightColor);
    grad.addColorStop(0.4, color);
    grad.addColorStop(1, `${color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, radius, 0, Math.PI * 2);
    ctx.fill();

    ProjectileEffect.coreGlowCache.set(key, { canvas, zoom: bucketZoom });
    return canvas;
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = camera.worldToScreenInPlace(this.position.x, this.position.y);
    const sx = screenPos.x;
    const sy = screenPos.y;

    const ctx = renderer.context;
    ctx.save();

    const element = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveElement(this.skill)
      : SkillCreator.getEffectiveElement(this.skill as CustomSkill);
    const color = this.getElementColor(element);
    const brightColor = this.getBrightColor(element);

    // Draw trail with gradient (read from circular buffer)
    if (this.trailCount > 1) {
      for (let i = 1; i < this.trailCount; i++) {
        const prevIdx = (this.trailHead - this.trailCount + i - 1 + ProjectileEffect.TRAIL_MAX) % ProjectileEffect.TRAIL_MAX;
        const currIdx = (this.trailHead - this.trailCount + i + ProjectileEffect.TRAIL_MAX) % ProjectileEffect.TRAIL_MAX;
        const prevScreen = camera.worldToScreenInPlace(this.trailBuffer[prevIdx].x, this.trailBuffer[prevIdx].y);
        const prevX = prevScreen.x;
        const prevY = prevScreen.y;
        const currScreen = camera.worldToScreenInPlace(this.trailBuffer[currIdx].x, this.trailBuffer[currIdx].y);
        const currX = currScreen.x;
        const currY = currScreen.y;
        const alpha = i / this.trailCount;
        const width = (this.size * 0.8 * alpha) * camera.zoom;

        ctx.beginPath();
        ctx.moveTo(prevX, prevY);
        ctx.lineTo(currX, currY);
        ctx.strokeStyle = `${color}${Math.floor(alpha * 150).toString(16).padStart(2, '0')}`;
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      // Element-specific trail particles along the path
      this.renderTrailParticles(ctx, camera, element, color, brightColor);
    }

    // Outer glow (cached)
    const outerCanvas = this.getOuterGlowCanvas(color, camera.zoom);
    const outerHalf = outerCanvas.width / 2;
    ctx.drawImage(outerCanvas, sx - outerHalf, sy - outerHalf);

    // Core glow (cached)
    const coreCanvas = this.getCoreGlowCanvas(color, brightColor, camera.zoom);
    const coreHalf = coreCanvas.width / 2;
    ctx.drawImage(coreCanvas, sx - coreHalf, sy - coreHalf);

    // Rotating energy lines
    ctx.strokeStyle = brightColor;
    ctx.lineWidth = 1.5 * camera.zoom;
    for (let i = 0; i < 4; i++) {
      const angle = this.rotation + (Math.PI / 2) * i;
      const innerR = this.size * 0.3 * camera.zoom;
      const outerR = this.size * 1.2 * camera.zoom;

      ctx.beginPath();
      ctx.moveTo(
        sx + Math.cos(angle) * innerR,
        sy + Math.sin(angle) * innerR
      );
      ctx.lineTo(
        sx + Math.cos(angle) * outerR,
        sy + Math.sin(angle) * outerR
      );
      ctx.stroke();
    }

    // Bright center
    ctx.beginPath();
    ctx.arc(sx, sy, this.size * 0.4 * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    ctx.restore();
  }

  /** Render element-specific trail particles behind the projectile */
  private renderTrailParticles(
    ctx: CanvasRenderingContext2D,
    camera: Camera,
    element: string,
    color: string,
    _brightColor: string
  ): void {
    const zoom = camera.zoom;
    const time = this.rotation; // Reuse rotation as time proxy

    // Sample 3-4 points along the trail for particle emission
    const count = Math.min(4, this.trailCount);
    for (let i = 0; i < count; i++) {
      const idx = (this.trailHead - 1 - i * 2 + ProjectileEffect.TRAIL_MAX * 3) % ProjectileEffect.TRAIL_MAX;
      if (i * 2 >= this.trailCount) break;
      const tp = this.trailBuffer[idx];
      const sp = camera.worldToScreenInPlace(tp.x, tp.y);
      const fadeAlpha = 1 - (i / count);

      switch (element) {
        case 'od': {
          // Fire: small sparks drifting up
          const sparkY = sp.y - (time * 20 + i * 8) % (15 * zoom);
          const sparkX = sp.x + Math.sin(time * 5 + i * 3) * 3 * zoom;
          ctx.fillStyle = `rgba(255, 180, 50, ${fadeAlpha * 0.7})`;
          ctx.beginPath();
          ctx.arc(sparkX, sparkY, (1.5 - i * 0.3) * zoom, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 'su': {
          // Ice: small crystals
          ctx.save();
          ctx.translate(sp.x, sp.y);
          ctx.rotate(time * 2 + i);
          ctx.fillStyle = `rgba(150, 220, 255, ${fadeAlpha * 0.6})`;
          const s = (1.5 - i * 0.3) * zoom;
          ctx.fillRect(-s / 2, -s / 2, s, s);
          ctx.restore();
          break;
        }
        case 'gok': {
          // Lightning: tiny spark flashes
          if (Math.sin(time * 15 + i * 7) > 0.3) {
            ctx.fillStyle = `rgba(200, 230, 255, ${fadeAlpha * 0.8})`;
            ctx.beginPath();
            ctx.arc(sp.x + Math.sin(i * 13) * 3 * zoom, sp.y + Math.cos(i * 17) * 3 * zoom, 1.5 * zoom, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'karanlik': {
          // Dark/Chaos: flickering purple motes
          const flicker = Math.sin(time * 8 + i * 4) * 0.5 + 0.5;
          ctx.fillStyle = `rgba(180, 100, 255, ${fadeAlpha * flicker * 0.7})`;
          ctx.beginPath();
          ctx.arc(sp.x + Math.sin(i * 5) * 3 * zoom, sp.y, (1 + flicker) * zoom, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        default: {
          // Physical: small grey particles
          ctx.fillStyle = `${color}${Math.floor(fadeAlpha * 100).toString(16).padStart(2, '0')}`;
          ctx.beginPath();
          ctx.arc(sp.x, sp.y + Math.sin(time + i) * 2 * zoom, 1 * zoom, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
      }
    }
  }

  private getElementColor(element: string): string {
    return CIRCLE_ELEMENT_COLORS[element] || '#cccccc';
  }

  private getBrightColor(element: string): string {
    return BRIGHT_COLORS[element] || '#ffffff';
  }

  getPosition(): Vector2 {
    return { ...this.position };
  }
}

// Line AOE effect - beam-like area from player toward target
export class LineEffect extends SkillEffect {
  private lineLength: number;
  private lineWidth: number;
  private color: string;
  private endPos: Vector2;

  constructor(config: SkillEffectConfig) {
    super(config);
    this.maxLife = 0.5;
    this.life = this.maxLife;

    if (isSkillV2(this.skill)) {
      this.lineLength = SkillCreatorV2.getEffectiveRange(this.skill);
      this.lineWidth = SkillCreatorV2.getEffectiveArea(this.skill) || 40;
    } else {
      this.lineLength = 150;
      this.lineWidth = 40;
    }

    this.endPos = {
      x: this.origin.x + this.direction.x * this.lineLength,
      y: this.origin.y + this.direction.y * this.lineLength,
    };

    const element = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveElement(this.skill)
      : SkillCreator.getEffectiveElement(this.skill as CustomSkill);
    this.color = CIRCLE_ELEMENT_COLORS[element] || '#ffcc88';
  }

  update(deltaTime: number): void {
    this.life -= deltaTime;
    if (this.life <= 0) this.isComplete = true;
  }

  render(renderer: Renderer, camera: Camera): void {
    const ctx = renderer.context;
    const progress = this.getProgress();
    const alpha = Math.max(0, 1 - progress);
    const zoom = camera.zoom;

    const start = camera.worldToScreen(this.origin.x, this.origin.y);
    const end = camera.worldToScreen(this.endPos.x, this.endPos.y);

    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const angle = Math.atan2(dy, dx);
    const length = Math.sqrt(dx * dx + dy * dy);
    const width = this.lineWidth * zoom;

    ctx.save();
    ctx.globalAlpha = alpha * 0.7;
    ctx.translate(start.x, start.y);
    ctx.rotate(angle);

    // Expanding sweep effect
    const sweep = Math.min(1, progress * 3); // sweeps full length in first 33%

    // Main beam body
    const grad = ctx.createLinearGradient(0, 0, length * sweep, 0);
    grad.addColorStop(0, this.color + 'cc');
    grad.addColorStop(0.7, this.color + '88');
    grad.addColorStop(1, this.color + '00');
    ctx.fillStyle = grad;
    ctx.fillRect(0, -width / 2, length * sweep, width);

    // Bright core
    ctx.globalAlpha = alpha;
    const coreGrad = ctx.createLinearGradient(0, 0, length * sweep, 0);
    coreGrad.addColorStop(0, '#ffffffcc');
    coreGrad.addColorStop(0.3, this.color + 'aa');
    coreGrad.addColorStop(1, this.color + '00');
    ctx.fillStyle = coreGrad;
    ctx.fillRect(0, -width / 4, length * sweep, width / 2);

    // Edge lines
    ctx.globalAlpha = alpha * 0.5;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 2 * zoom;
    ctx.beginPath();
    ctx.moveTo(0, -width / 2);
    ctx.lineTo(length * sweep, -width / 2);
    ctx.moveTo(0, width / 2);
    ctx.lineTo(length * sweep, width / 2);
    ctx.stroke();

    ctx.restore();
  }
}

// Ground slam / wave effect
export class WaveEffect extends SkillEffect {
  private maxRadius: number;

  constructor(config: SkillEffectConfig) {
    super(config);
    this.maxLife = 0.5;
    this.life = this.maxLife;

    this.maxRadius = isSkillV2(this.skill)
      ? SkillCreatorV2.getEffectiveArea(this.skill)
      : SkillCreator.getEffectiveArea(this.skill as CustomSkill);
  }

  update(deltaTime: number): void {
    this.life -= deltaTime;

    if (this.life <= 0) {
      this.isComplete = true;
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = camera.worldToScreenInPlace(this.origin.x, this.origin.y);
    const sx = screenPos.x;
    const sy = screenPos.y;
    const progress = this.getProgress();
    const alpha = 1 - progress;

    const ctx = renderer.context;
    ctx.save();

    // Multiple expanding rings
    for (let i = 0; i < 3; i++) {
      const ringProgress = Math.max(0, progress - i * 0.1);
      if (ringProgress <= 0) continue;

      const ringRadius = this.maxRadius * ringProgress * camera.zoom;
      const ringAlpha = alpha * (1 - i * 0.3);

      ctx.beginPath();
      ctx.arc(sx, sy, ringRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(200, 150, 100, ${ringAlpha})`;
      ctx.lineWidth = (4 - i) * camera.zoom;
      ctx.stroke();
    }

    // Center impact
    if (progress < 0.3) {
      const impactRadius = 20 * camera.zoom * (1 - progress / 0.3);
      ctx.beginPath();
      ctx.arc(sx, sy, impactRadius, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 200, 100, ${0.5 * (1 - progress / 0.3)})`;
      ctx.fill();
    }

    ctx.restore();
  }
}

// Map skill base types to visual effect shapes
function getEffectShapeFromBaseType(baseType: string): 'slash' | 'circle' | 'projectile' | 'wave' {
  const melee = ['slash', 'cleave', 'spin', 'thrust', 'slam', 'stab', 'backstab', 'bash', 'block_counter', 'charge', 'melee'];
  const aoe = ['aoe', 'explosion', 'shockwave', 'totem', 'summon', 'trap', 'aura', 'buff', 'rain'];
  const projectile = ['projectile', 'arrow', 'multishot'];
  const line = ['beam', 'chain'];

  if (melee.includes(baseType)) return 'slash';
  if (aoe.includes(baseType)) return 'circle';
  if (projectile.includes(baseType)) return 'projectile';
  if (line.includes(baseType)) return 'wave';
  return 'slash'; // default
}

// Factory function to create appropriate effect
export function createSkillEffect(config: SkillEffectConfig): SkillEffect {
  // Get the base type - V2 uses deliveryType, V1 uses baseType
  const baseType = isSkillV2(config.skill)
    ? config.skill.deliveryType
    : (config.skill as CustomSkill).baseType;

  // V2 melee strike: no slash arc, just a very short subtle hit flash
  if (isSkillV2(config.skill) && baseType === 'melee') {
    const meleeType = (config.skill as any).meleeType || 'strike';
    if (meleeType === 'strike') {
      // Minimal hit effect - tiny fast slash toward target
      const s = new SlashEffect(config);
      s.setMinimal(true); // flag for reduced visual
      return s;
    }
  }

  const effectShape = getEffectShapeFromBaseType(baseType);

  switch (effectShape) {
    case 'slash':
      return new SlashEffect(config);
    case 'circle':
      // For V2 AOE skills, check the shape
      if (isSkillV2(config.skill) && config.skill.deliveryType === 'aoe') {
        const aoeShape = SkillCreatorV2.getAOEShape(config.skill);
        if (aoeShape === 'cone') {
          return new ConeEffect(config);
        }
        if (aoeShape === 'line') {
          return new LineEffect(config);
        }
      }
      return new CircleEffect(config);
    case 'projectile':
      return new ProjectileEffect(config);
    case 'wave':
      return new WaveEffect(config);
    default:
      return new SlashEffect(config);
  }
}
